import React from 'react';
import { View, Text, ViewStyle } from 'react-native';
import PressableScale from '@shared/components/md-design-system/Button/PressableScaleButton';
import { colorPalette } from '@shared/config/colorsConfig';

export interface TabItem<T extends string> {
  key: T;
  label: string;
}

export interface TabBarProps<T extends string> {
  tabs: TabItem<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  style?: ViewStyle;
  containerClassName?: string;
  tabClassName?: string;
  activeTabClassName?: string;
  inactiveTabClassName?: string;
  borderRadius?: number;
  gap?: number;
}

export function TabBar<T extends string>({
  tabs,
  activeTab,
  onTabChange,
  style,
  containerClassName = '',
  tabClassName = '',
  activeTabClassName = '',
  inactiveTabClassName = '',
  borderRadius = 20,
  gap = 12,
}: TabBarProps<T>) {
  return (
    <View
      style={[{ flexDirection: 'row', gap }, style]}
      className={containerClassName}
    >
      {tabs.map((tab) => {
        const isActive = activeTab === tab.key;

        return (
          <PressableScale
            key={tab.key}
            onPress={() => onTabChange(tab.key)}
            borderRadius={borderRadius}
            rippleColor={colorPalette.secondary[100]}
            pressedTintColor={colorPalette.secondary[100]}
            frameStyle={{
              flex: 1,
              borderRadius: borderRadius,
              borderWidth: 1,
              borderColor: isActive ? colorPalette.primary[600] : colorPalette.gray[300],
              backgroundColor: isActive ? colorPalette.secondary[100] : colorPalette.white,
              height: 56,
            }}
            frameClassName={`${tabClassName} ${isActive ? activeTabClassName : inactiveTabClassName}`}
            pressedFrameClassName={isActive ? 'border-primary-600' : 'border-secondary-300'}
            contentStyle={{
              paddingHorizontal: 24,
              paddingVertical: 12,
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: borderRadius,
              height: 56,
            }}
          >
            {({ pressed }) => (
              <Text
                className="text-base font-semibold"
                style={{
                  color: isActive
                    ? colorPalette.primary[600]
                    : pressed
                      ? colorPalette.secondary[500]
                      : colorPalette.gray[500],
                  textAlign: 'center',
                }}
              >
                {tab.label}
              </Text>
            )}
          </PressableScale>
        );
      })}
    </View>
  );
}
